import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { ROUTER_UNIVERSAL_PRODUCTS } from 'app/AppRouter';
import { useUniversalProductsTree } from 'domains/catalog/Catalog.requests';
import { resetUniversalProductsFilters } from 'domains/catalog/Catalog.store';
import LandingPage from 'pages/UniversalProductsPage/LandingPage';
import ProductsPage from 'pages/UniversalProductsPage/ProductsPage';
import { Container, midPadding } from 'UI';
import { getData } from 'utils';
import { useResetScroll } from 'utils/hooks/useResetScroll';

const UniversalProductsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const previousPathnameRef = useRef<string | undefined>(undefined);
  const { level1, level2, level3 } = useParams<{ level1?: string; level2?: string; level3?: string }>();
  const universalProductsTree = useUniversalProductsTree();
  const isData = useMemo(() => {
    if (!level1 || !level2) {
      return false;
    }
    if (level3) {
      return true;
    }
    const tree = getData(universalProductsTree);
    const level1Category = tree?.find((category) => category.nodeId === level1);
    const level2Category = level1Category?.childCategories?.find((category) => category.nodeId === level2);
    const hasChildren = (level2Category?.childCategories?.length ?? 0) > 0;
    return Boolean(level2Category) && !hasChildren;
  }, [level1, level2, level3, universalProductsTree]);

  useEffect(() => {
    const pathnameChanged = previousPathnameRef.current !== location.pathname;
    previousPathnameRef.current = location.pathname;

    if (!pathnameChanged) {
      return;
    }

    dispatch(resetUniversalProductsFilters());
    if (location.search) {
      history.replace({
        pathname: location.pathname,
        search: '',
      });
    }
  }, [dispatch, history, location.pathname, location.search]);

  useResetScroll();

  return (
    <Container padding={midPadding} size={'xxl'}>
      <Switch>
        {isData && (
          <Route path={`${ROUTER_UNIVERSAL_PRODUCTS}/:level1/:level2/:level3?`} render={() => <ProductsPage />} />
        )}
        <Route path="*" render={() => <LandingPage level1={level1} />} />
      </Switch>
    </Container>
  );
};

export default UniversalProductsPage;
